// Правка шаблона ведомости: назначение объекту и состав колонок.
//
// Живёт в разделе «Объекты», потому что настраивают шаблон отсюда — с карточки объекта (тз,
// раздел 5.2). Сами двери принадлежат расчёту, и это правильно: шаблон один на много объектов,
// а ведомость по нему собирает «Расчёт».

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import type {
  PayrollTemplateOut,
  SiteTemplateIn,
  SiteTemplateOut,
  TemplateColumnsIn,
} from '@/shared/api/endpoints/payroll';
import { setSiteTemplate, setTemplateColumns } from '@/shared/api/endpoints/payroll';
import type { ApiError } from '@/shared/errors/api-error';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

/**
 * Сброс кэша после правки шаблона.
 *
 * Целиком организационный справочник И расчёт: сменился шаблон объекта — поменялся набор
 * колонок его ведомости, а он приезжает вместе с сеткой. Обновлять что-то одно значит
 * оставить на экране сетку по старому шаблону рядом с новой подписью.
 */
function useСброс() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['org'] });
    void client.invalidateQueries({ queryKey: ['payroll'] });
  };
}

export function useSetSiteTemplate(): UseMutationResult<
  SiteTemplateOut,
  ApiError,
  { siteId: string; body: SiteTemplateIn }
> {
  const сбросить = useСброс();
  return useMutation<SiteTemplateOut, ApiError, { siteId: string; body: SiteTemplateIn }>({
    mutationFn: ({ siteId, body }) => setSiteTemplate(siteId, body),
    meta: подтверждение('Шаблон объекта сохранён'),
    onSuccess: сбросить,
  });
}

export function useSetTemplateColumns(): UseMutationResult<
  PayrollTemplateOut,
  ApiError,
  { templateId: string; body: TemplateColumnsIn }
> {
  const сбросить = useСброс();
  return useMutation<PayrollTemplateOut, ApiError, { templateId: string; body: TemplateColumnsIn }>(
    {
      mutationFn: ({ templateId, body }) => setTemplateColumns(templateId, body),
      meta: подтверждение('Колонки шаблона сохранены'),
      onSuccess: сбросить,
    },
  );
}
